import type { FC } from 'react';
import React, { createContext, useContext, useMemo } from 'react';

export interface LoginProps {
  user: {
    name?: string;
    lastNames?: string;
    email?: string | null;
    membership?: object | null;
    isLoading: boolean;
    isFetching: boolean;
    isLogged: boolean;
    isRemembered: boolean;
    isPrime: boolean;
    seedOverride?: string;
  };
}

export interface LoginProviderProps {
  loginProps: LoginProps;
}

export type LoginProviderContext = LoginProviderProps['loginProps']['user'];

const LoginProviderContext = createContext<LoginProviderContext | null>(null);

const LoginProvider: FC<LoginProviderProps> = ({ loginProps, children }) => {
  const values = useMemo<LoginProviderContext>(() => {
    if (!loginProps || !loginProps.user) {
      return {
        isLoading: false,
        isFetching: false,
        isLogged: false,
        isRemembered: false,
        isPrime: false,
        name: '',
        lastNames: '',
        email: '',
        membership: null,
      };
    }

    const {
      isLoading,
      isFetching,
      isLogged,
      isRemembered,
      isPrime,
      name,
      lastNames,
      email,
      membership,
      seedOverride,
    } = loginProps.user;

    return {
      isLoading,
      isFetching,
      isLogged,
      isRemembered,
      isPrime,
      name,
      lastNames,
      email,
      membership,
      seedOverride,
    };
  }, [loginProps]);

  return (
    <LoginProviderContext.Provider value={values}>
      {children}
    </LoginProviderContext.Provider>
  );
};

export const useLogin = (): LoginProviderContext => {
  const context = useContext(LoginProviderContext);
  if (!context) {
    throw new Error('must be used within a LoginProvider');
  }

  return context;
};

export default LoginProvider;
